// MACHINERY V2 (Stroje) — the 38px form select (M2.6).
//
// >>> SHARED V2 PRIMITIVE (the FormField kit). IMPORT IT — DO NOT REINVENT. <<<
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.6. Reference impl:
// design_handoff_machinery_v3/machinery-sr.jsx `FormSelect` (lines 653-665) —
// a 38px control showing the placeholder or the chosen label plus a chevron.
// Artboards 0702 / 0703 draw it twice: URGENCIA and DOTKNUTÁ SÚČASŤ.
//
// RADIX, NOT `<select>` AND NOT MUI. §3 pins the v2 primitive library to Radix;
// a native `<select>` cannot carry the coloured urgency dot inside its options,
// and MUI is out of the v2 stack entirely. Same choice, same reasons, as
// profile/tabs/TabSelect.tsx — which is NOT reused here because it is the
// 32px tab-toolbar control with an eyebrow INSIDE the control; this is the
// 38px form control with the eyebrow ABOVE it (FormField owns that label).
//
// TYPEAHEAD REPLACES v1's AUTOCOMPLETE, BY DESIGNER DIRECTION. v1 renders the
// affected-part field as a grouped MUI `Autocomplete` with substring filtering;
// artboards 0702 / 0703 draw a plain select, and the artboard governs look +
// behaviour (ticket "Golden rule"). Radix DropdownMenu ships prefix typeahead
// and roving arrow-key focus for free, so "type to find" survives — as a prefix
// match rather than a substring one. The BINDING is unchanged (`part.id` ->
// `affectedPartCategoryId`), which is what the data-parity rule protects.
//
// PORTAL RULE (MachineryV2Layout.tsx:20-39): DropdownMenu.Content portals to
// document.body, outside `.machinery-v2-root`, where the scoped vars do not
// resolve. Content re-carries the scope classes, sets a transparent background,
// paints on the inner `.ms-menu` div and reads `palette` LIVE on every render.
// Same countermeasure as chrome/ChromeRightMenus.tsx `MenuContent` (canonical),
// fleet/FleetCardMenu.tsx and profile/tabs/TabSelect.tsx.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";

export interface FormSelectOption {
  /** The value written back by onChange. */
  id: string;
  label: string;
  /** Group heading this option sits under; undefined = ungrouped. */
  group?: string;
  /** Optional leading dot (the urgency palette). */
  dotColor?: string;
  /** Rendered but not choosable — the affected-part fallback rows, which carry
   *  no categoryId and must never be sent (sr/srMachineParts.ts). */
  disabled?: boolean;
}

export interface FormSelectProps {
  id?: string;
  /** Shown when nothing is selected — `urgencyNone` / `affectedMachinePart`. */
  placeholder: string;
  /** Accessible name for the trigger; the visible eyebrow lives in FormField. */
  ariaLabel: string;
  value?: string;
  options: FormSelectOption[];
  onChange: (value?: string) => void;
  /** Renders a "clear" row above the list (urgency is optional; the part is not). */
  clearLabel?: string;
  invalid?: boolean;
  describedBy?: string;
}

const Chevron: React.FC = () => (
  <svg
    viewBox="0 0 12 12"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    style={{ flexShrink: 0, color: "var(--muted)" }}
    aria-hidden
  >
    <path d="M3 4.5 L6 7.5 L9 4.5" />
  </svg>
);

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden
    style={{
      width: 8,
      height: 8,
      borderRadius: 999,
      background: color,
      flexShrink: 0,
      marginRight: 8,
    }}
  />
);

/** Groups keep the artboard's order — the FIRST appearance of each group name
 *  wins, so callers control ordering by the order they build `options`. */
const groupOptions = (options: FormSelectOption[]): { name?: string; items: FormSelectOption[] }[] => {
  const out: { name?: string; items: FormSelectOption[] }[] = [];
  options.forEach((o) => {
    const last = out.length > 0 ? out[out.length - 1] : undefined;
    if (last && last.name === o.group) last.items.push(o);
    else out.push({ name: o.group, items: [o] });
  });
  return out;
};

const FormSelect: React.FC<FormSelectProps> = ({
  id,
  placeholder,
  ariaLabel,
  value,
  options,
  onChange,
  clearLabel,
  invalid,
  describedBy,
}) => {
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);
  const selected = options.find((o) => o.id === value);
  const groups = groupOptions(options);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          id={id}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={"mv2-input" + (invalid ? " is-invalid" : "")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 0,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          {selected && selected.dotColor && <Dot color={selected.dotColor} />}
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: selected ? "var(--ink)" : "var(--faint)",
            }}
          >
            {selected ? selected.label : placeholder}
          </span>
          <Chevron />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          align="start"
          sideOffset={6}
          onClick={(e) => e.stopPropagation()}
          // Z-INDEX 1400, NOT the 1290 every other v2 menu uses. DO NOT
          // "normalise" this back — 1290 is the PAGE-level menu tier and it is
          // correct for profile/tabs/TabSelect.tsx:116, fleet/FleetCardMenu.tsx:161
          // and chrome/chromeMenuContent.tsx:38, all of which only ever open on a
          // page. FormSelect is the one that also opens INSIDE a dialog, and
          // profile/tabs/TabDialog.tsx renders Overlay at zIndex 1300 and Content
          // at 1301 (:95 / :107). Both are body-level Radix portals — positioned
          // siblings in ONE stacking context — so at 1290 this menu painted
          // BEHIND the dialog: Radix opened it, the user saw nothing, and it was
          // indistinguishable from "the dropdown has no options" (the owner's
          // report for TYP STROJA in the custom-machine dialog; it hit every
          // select in add/CustomMachineDialog.tsx and catalog/CatalogAddDialogV2.tsx,
          // both of which use TabDialog). 1400 is the house dialog-borne-menu
          // tier: midas/components/v3/ModelPickerV3.tsx:478, v3/RailRowMenuV3.tsx:226,
          // v3/NewChatReadinessCluster.tsx:200, settings/v2/atoms/SettingsV2Tooltip.tsx:47.
          // feedback/ToastV2.tsx:117 stays above at 1500, so a toast still wins.
          // THE FAILURE MODE TO REMEMBER: a shared primitive built for one page
          // context (M2.6 SrCreateV2, where nothing sits above 1290) reused inside
          // a dialog in M2.7 — a type check, a build and a diff read all see an
          // unchanged constant. New host above 1301? Re-check this number.
          style={{ background: "transparent", zIndex: 1400 }}
        >
          <div
            className="ms-menu"
            role="none"
            style={{ width: 320, maxWidth: "min(320px, 92vw)", maxHeight: 340, overflowY: "auto" }}
          >
            {clearLabel && (
              <DropdownMenu.Item className="mi" onSelect={() => onChange(undefined)}>
                <span style={{ color: "var(--muted)" }}>{clearLabel}</span>
              </DropdownMenu.Item>
            )}
            {groups.map((g, gi) => (
              <DropdownMenu.Group key={g.name || "_" + gi}>
                {g.name && (
                  <DropdownMenu.Label
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 9,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--faint)",
                      padding: "8px 10px 3px",
                    }}
                  >
                    {g.name}
                  </DropdownMenu.Label>
                )}
                {/* KEY IS POSITIONAL, NOT `o.id` (M2.6 review C9). An option id is
                    NOT unique: the affected-part FALLBACK rows all carry `id: ""`
                    (sr/srMachineParts.ts:129), so before `GET /v1/machine-parts`
                    resolves every one of the ~48 rows would share the key "" —
                    React logs duplicate keys and can reconcile the wrong node
                    when the real list replaces the fallback. Group + index + id
                    is unique by construction for any option set. */}
                {g.items.map((o, oi) => (
                  <DropdownMenu.Item
                    key={gi + "-" + oi + "-" + o.id}
                    className="mi"
                    onSelect={() => onChange(o.id)}
                    disabled={o.disabled}
                    textValue={o.label}
                    style={{
                      ...(o.id === value ? { color: "var(--mod-accent)", fontWeight: 600 } : null),
                      ...(o.disabled ? { opacity: 0.45, cursor: "default" } : null),
                    }}
                  >
                    {o.dotColor && <Dot color={o.dotColor} />}
                    <span>{o.label}</span>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Group>
            ))}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default FormSelect;
